import { useState } from 'react'
import { ROTULO_STATUS_NEGOCIACAO, type NegociacaoDto } from '@crm/shared'
import { useHistoricoDoContato } from '../../api/hooks-leads'
import { Carregando, Etiqueta, juntar } from '../../componentes/ui'
import { formatarData, formatarMoeda } from '../../formato'
import { CLASSE_DO_CABECALHO_DA_TABELA } from '../../componentes/pagina'

/**
 * Todos os ciclos de venda de um cliente, do mais recente para o mais antigo.
 *
 * É a aba que só passou a fazer sentido depois que a negociação virou um
 * registro próprio: antes, cada venda apagava a anterior do card, e a única
 * forma de saber que o cliente já tinha comprado era abrir o relatório.
 *
 * Esta é a tela que responde a pergunta que o vendedor faz antes de atender:
 * "o que este cliente já comprou, e por quanto?".
 */

const COR_DO_STATUS = {
  aberta: 'azul',
  ganha: 'verde',
  perdida: 'vermelha',
} as const

export function HistoricoDeNegociacoes({
  contatoId,
  destacar,
}: {
  contatoId: string
  /** O ciclo que está aberto no card, para não se perder no meio dos outros. */
  destacar?: string | null
}) {
  const { data, isLoading } = useHistoricoDoContato(contatoId)

  if (isLoading) return <Carregando texto="Carregando o histórico…" />

  /*
    Cronológico aqui, ao contrário do servidor.

    Lá a ordem é "abertas primeiro", para o quadro não perder cartão em jogo
    quando a paginação corta. No histórico a leitura é outra: a linha do tempo
    do cliente, do mais recente para o mais antigo.
  */
  const negociacoes = [...(data?.itens ?? [])].sort((a, b) => b.abertaEm.localeCompare(a.abertaEm))
  if (negociacoes.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-tinta-400">
        Nenhuma negociação ainda. Use “Nova negociação” para abrir a primeira.
      </p>
    )
  }

  const ganhas = negociacoes.filter((n) => n.status === 'ganha')
  const totalGanho = ganhas.reduce((soma, n) => soma + (n.desfecho?.valorTotal ?? 0), 0)
  /*
    A perda entra no resumo também (pedido de 18/10/2026): quantas vezes o
    cliente escapou e quanto valiam essas negociações. O valor é o do card na
    hora da perda, porque perda não tem venda registrada para somar.
  */
  const perdidas = negociacoes.filter((n) => n.status === 'perdida')
  const totalPerdido = perdidas.reduce((soma, n) => soma + (n.valor ?? 0), 0)

  return (
    <div className="space-y-3">
      {/*
        O resumo em cima porque é o que se olha primeiro: quantas vezes já
        comprou, quantas escapou e quanto isso deu. A lista embaixo é para
        quando a resposta curta não basta. As contagens ficam numa linha e os
        valores na outra: cinco números lado a lado não cabem na janela.
      */}
      <div className="grid grid-cols-6 gap-x-2 gap-y-3 rounded-md bg-tinta-50 p-3 text-center ring-1 ring-tinta-200">
        <div className="col-span-2">
          <p className="text-lg font-semibold text-tinta-900">{negociacoes.length}</p>
          <p className="text-xs text-tinta-400">negociações</p>
        </div>
        <div className="col-span-2">
          <p className="text-lg font-semibold text-green-700">{ganhas.length}</p>
          <p className="text-xs text-tinta-400">ganhas</p>
        </div>
        <div className="col-span-2">
          <p className="text-lg font-semibold text-red-700">{perdidas.length}</p>
          <p className="text-xs text-tinta-400">perdidas</p>
        </div>
        <div className="col-span-3 border-t border-tinta-200 pt-3">
          <p className="text-lg font-semibold text-tinta-900">{formatarMoeda(totalGanho)}</p>
          <p className="text-xs text-tinta-400">total comprado</p>
        </div>
        <div className="col-span-3 border-t border-tinta-200 pt-3">
          <p className="text-lg font-semibold text-red-700">{formatarMoeda(totalPerdido)}</p>
          <p className="text-xs text-tinta-400">valor perdido</p>
        </div>
      </div>

      <ul className="space-y-2">
        {negociacoes.map((negociacao) => (
          <li key={negociacao.id}>
            <LinhaDoHistorico negociacao={negociacao} destacada={negociacao.id === destacar} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function LinhaDoHistorico({
  negociacao,
  destacada,
}: {
  negociacao: NegociacaoDto
  destacada: boolean
}) {
  /*
    Aberta já mostrando o detalhe quando é a do card, fechada só quando se
    clica. Empilhar tudo aberto faria a aba de um cliente antigo virar uma
    parede de tabelas; guardar tudo fechado obrigaria a clicar para descobrir
    o que se comprou, que é a pergunta que traz a pessoa aqui.
  */
  const [aberta, setAberta] = useState(destacada)
  const desfecho = negociacao.desfecho

  return (
    <article
      className={juntar(
        'overflow-hidden rounded-md border',
        destacada ? 'border-marca bg-marca/5' : 'border-tinta-200',
      )}
    >
      <button
        type="button"
        onClick={() => setAberta((a) => !a)}
        aria-expanded={aberta}
        className="w-full p-3 text-left hover:bg-tinta-50"
      >
        <div className="flex flex-wrap items-center gap-2">
          <Etiqueta cor={COR_DO_STATUS[negociacao.status]}>
            {ROTULO_STATUS_NEGOCIACAO[negociacao.status]}
          </Etiqueta>

          {/*
            O título só aparece quando existe. Sem ele, cair no nome do funil
            repetiria "Vendas | Vendas / Ganho" na mesma linha — e ciclo antigo,
            de antes de existir título, é justamente o que não tem.
          */}
          {negociacao.titulo ? (
            <span className="text-sm font-medium text-tinta-900">{negociacao.titulo}</span>
          ) : null}

          <span className="text-xs text-tinta-400">
            {negociacao.funilNome} / {negociacao.etapaNome}
          </span>

          <span className="ml-auto text-sm font-medium">{formatarMoeda(negociacao.valor)}</span>
          <span className="text-xs text-tinta-400">{aberta ? '▴' : '▾'}</span>
        </div>

        <p className="mt-1 text-xs text-tinta-400">
          Aberta em {formatarData(negociacao.abertaEm)}
          {negociacao.fechadaEm ? ` · fechada em ${formatarData(negociacao.fechadaEm)}` : ''}
          {destacada ? ' · é a que está aberta neste card' : ''}
        </p>
      </button>

      {aberta ? <Detalhe negociacao={negociacao} desfecho={desfecho} /> : null}
    </article>
  )
}

function Detalhe({
  negociacao,
  desfecho,
}: {
  negociacao: NegociacaoDto
  desfecho: NegociacaoDto['desfecho']
}) {
  if (negociacao.status === 'aberta') {
    return (
      <div className="border-t border-tinta-200 px-3 py-2 text-xs text-tinta-400">
        Ainda em aberto. O que foi vendido aparece aqui quando o card entrar em ganho.
      </div>
    )
  }

  if (!desfecho) {
    return (
      <div className="border-t border-tinta-200 px-3 py-2 text-xs text-tinta-400">
        Sem registro de desfecho para este ciclo.
      </div>
    )
  }

  if (desfecho.motivo || negociacao.status === 'perdida') {
    return (
      <div className="border-t border-tinta-200 px-3 py-2 text-xs">
        <p className="text-red-700">Motivo: {desfecho.motivo ?? 'não informado'}</p>
        {desfecho.observacao ? (
          <p className="mt-0.5 text-tinta-600">{desfecho.observacao}</p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="border-t border-tinta-200 px-3 py-2">
      {desfecho.itens.length === 0 ? (
        /*
          Venda registrada só com o valor, sem itens. É o caminho que a tela
          de ganho oferece quando ninguém adiciona item, e dizer isso é melhor
          do que uma tabela vazia: a pessoa vem aqui procurando o que foi
          vendido e precisa saber que não foi anotado.
        */
        <p className="text-xs text-tinta-400">
          Venda registrada só com o valor total, sem itens. Para o que foi vendido ficar no histórico,
          adicione os itens na hora de marcar o ganho.
        </p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className={CLASSE_DO_CABECALHO_DA_TABELA}>
              <th className="pb-1 font-medium">Item</th>
              <th className="pb-1 text-right font-medium">Qtd.</th>
              <th className="pb-1 text-right font-medium">Unitário</th>
              <th className="pb-1 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {desfecho.itens.map((item, i) => (
              <tr key={`${item.descricao}-${i}`} className="text-tinta-700">
                <td className="py-0.5">{item.descricao}</td>
                <td className="py-0.5 text-right tabular-nums">{item.quantidade}</td>
                <td className="py-0.5 text-right tabular-nums">
                  {formatarMoeda(item.precoUnitario)}
                </td>
                <td className="py-0.5 text-right tabular-nums">
                  {formatarMoeda(item.quantidade * item.precoUnitario)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <dl className="mt-2 space-y-0.5 border-t border-tinta-100 pt-2 text-xs">
        {desfecho.itens.length > 0 ? (
          <div className="flex justify-between text-tinta-600">
            <dt>Subtotal</dt>
            <dd className="tabular-nums">{formatarMoeda(desfecho.valorBruto)}</dd>
          </div>
        ) : null}
        {desfecho.desconto > 0 ? (
          <div className="flex justify-between text-tinta-600">
            <dt>Desconto</dt>
            <dd className="tabular-nums">−{formatarMoeda(desfecho.desconto)}</dd>
          </div>
        ) : null}
        <div className="flex justify-between font-medium text-tinta-900">
          <dt>Total</dt>
          <dd className="tabular-nums">{formatarMoeda(desfecho.valorTotal)}</dd>
        </div>
      </dl>
    </div>
  )
}
